"use client";
import { FC, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { useRouter } from "next/navigation";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import TInput from "@/components/atoms/input/TInput";
import TSelect from "@/components/atoms/select/TSelect";
import TTextArea from "@/components/atoms/text-area/TTextArea";
import TCheckbox from "@/components/atoms/checkbox/TCheckbox";
import TButton from "@/components/atoms/buttons/TButton";
import axios from "axios";
import { trimEnd } from "lodash";
import { env } from "@/env.mjs";
import { IContactFormRequest } from "@/api/dtos/contact-us.type";
import { contactUsPositionList } from "@/constants/contact-us-form";
import { contactUsSchema } from "@/app/api/validationSchemas";

import GoogleReCaptcha from "../common/GoogleReCaptcha";
import useGoogleReCaptcha from "@/hooks/useGoogleReCaptcha";
import TPhoneInput from "@/components/atoms/phone-input/TPhoneInput";
import { handleException, IAxiosError } from "@/utils/exception.helper";
import ConsentMessage from "@/components/common/ConsentMessage";
import { markUserAsLead, trackEvent } from "@/utils/analytics";
import { hashEmail, hashPhone } from "@/utils/hash";
import { hasMarketingConsent } from "@/utils/cookie.helper";
import { THANK_YOU_PATH } from "@/constants/conversion";

// User properties live in this localStorage key (see analytics.ts).
// Reading the latest snapshot at submit-time means we capture the most
// up-to-date industry/service interest even if the user browsed several
// pages before filling out the form.
const readUserProps = (): {
  industry_interest?: string;
  service_interest?: string;
  traffic_origin?: string;
  user_type?: string;
} => {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage?.getItem("cb_user_props");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

// -------------------------------------------------------------

type IContactUsFormProps = {};

type IDefaultValues = {
  firstName: string;
  lastName: string;
  companyName: string;
  workEmail: string;
  position: string | null;
  specificPosition?: string;
  description: string;
  phone?: string;
  emailConcent?: boolean;
};

const defaultValues: IDefaultValues = {
  firstName: "",
  lastName: "",
  companyName: "",
  workEmail: "",
  position: null,
  specificPosition: "",
  phone: "",
  description: "",
  emailConcent: true,
};

const ContactUsForm: FC<IContactUsFormProps> = () => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const router = useRouter();

  // Form-funnel instrumentation:
  //   form_start fires once on the first interaction (also doubles as
  //     begin_checkout — pairs with view_item/purchase for GA4 funnels).
  //   formStartedAt anchors time-to-submit so we can flag bot-like
  //     submissions (<3s = likely bot) and correlate friction with
  //     submission time in GA4.
  const hasStartedRef = useRef(false);
  const formStartedAtRef = useRef<number | null>(null);
  const wasSubmittedRef = useRef(false);

  const handleFormStart = () => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;
    formStartedAtRef.current = Date.now();
    trackEvent("form_start", { form_name: "contact_us" });
    // GA4 ecommerce alias — pairs with view_item (already fired on
    // service/industry pages) and purchase (generate_lead alias on
    // submit) to surface a proper funnel report.
    trackEvent("begin_checkout", { item_id: "contact_us_form" });
  };

  // form_abandon — fires on tab close / navigation if the user started
  // the form but never submitted. High-value signal for retargeting.
  useEffect(() => {
    const onBeforeUnload = () => {
      if (hasStartedRef.current && !wasSubmittedRef.current) {
        trackEvent("form_abandon", {
          form_name: "contact_us",
          seconds_in_form: formStartedAtRef.current
            ? Math.round((Date.now() - formStartedAtRef.current) / 1000)
            : 0,
        });
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  const {
    reCaptchaRef,
    isReCaptchaAvailable,
    isRecaptchaVarified,
    resetReCaptcha,
  } = useGoogleReCaptcha();

  const onSubmit = async (formData: IDefaultValues) => {
    const { success, token } = await isRecaptchaVarified();
    if (!success) return;

    try {
      setIsSubmitting(true);

      // Pull GA4-derived user properties from localStorage so the lead
      // arrives in HubSpot / Slack with full context (industry interest,
      // traffic origin, etc.) without sales having to dig for it.
      const props = readUserProps();

      // Enhanced Conversions — hash email + phone client-side. The raw
      // values still go to the API (for HubSpot + email), but the dataLayer
      // event carries ONLY the hashes so Google Ads can match conversions
      // to ad clicks without us ever forwarding raw PII to GA/Ads.
      const [hashedEmail, hashedPhone] = await Promise.all([
        hashEmail(formData.workEmail || ""),
        hashPhone(formData.phone || ""),
      ]);

      await axios.post<IContactFormRequest>(
        `${trimEnd(env.NEXT_PUBLIC_SITE_URL, "/")}/api/contact-us`,
        {
          ...formData,
          industryInterest: props.industry_interest,
          serviceInterest: props.service_interest,
          trafficOrigin: props.traffic_origin,
          userType: props.user_type,
          pageUrl:
            typeof window !== "undefined" ? window.location.href : undefined,
          hashedEmail,
          hashedPhone,
        },
        {
          headers: token ? { "x-recaptcha-token": token } : undefined,
        },
      );
      wasSubmittedRef.current = true;
      // THE conversion event. GA4 marks generate_lead as a key event and
      // Google Ads imports it as the single primary conversion, so this is
      // the one signal that has to be right. Fires exactly once, and only
      // after the API confirmed success above — never on click.
      //
      // lead_source / form_id carry which funnel produced the lead so GA4
      // can segment without needing separate event names per form.
      // No raw PII — only SHA-256 hashes for Enhanced Conversions matching.
      trackEvent("generate_lead", {
        lead_source: "contact_form",
        // LEGACY (kept for rollback) — form_id previously duplicated
        // lead_source. Spec requires the form's DOM id, which is
        // id="contact-us-form" on the <form> element below.
        // form_id: "contact_form",
        form_id: "contact-us-form",
        form_name: "contact_us",
        position: formData.position || "unspecified",
        has_phone: Boolean(formData.phone),
        has_company: Boolean(formData.companyName),
        time_to_submit_s: formStartedAtRef.current
          ? Math.round((Date.now() - formStartedAtRef.current) / 1000)
          : 0,
        // Enhanced Conversions hashes — picked up by GTM and forwarded to
        // Google Ads as user-provided data for click→conversion match. Only
        // added to the dataLayer with Marketing consent (advertising use of
        // PII); without it these keys are omitted entirely, so no email/phone-
        // derived value — even hashed — is exposed to any tag. The raw values
        // still reach our own /api/contact-us (CRM), which the user's form
        // submission itself authorizes.
        sha256_email_address:
          hasMarketingConsent() && hashedEmail ? hashedEmail : undefined,
        sha256_phone_number:
          hasMarketingConsent() && hashedPhone ? hashedPhone : undefined,
      });
      // Sticky user-property — flips visitor cohort to "lead" so any
      // future session is segmentable as a returning lead.
      markUserAsLead();
      reset();

      // Land on the canonical thank-you page.
      //
      // This is what makes the Google Ads "Submit lead form" conversion
      // (a page-load trigger on /thank-you) actually record. Previously the
      // form only showed a toast and stayed put, so NO submit ever reached
      // /thank-you and the conversion counted zero regardless of how the
      // trigger URL was written.
      //
      // Deliberately a bare path with no query string so the Ads trigger can
      // match on an exact URL. Segmentation (which form, which intent) is
      // carried by the GA4 events above, not by the URL.
      //
      // Events are pushed to the dataLayer synchronously before this call and
      // gtag uses sendBeacon, so they survive the navigation. The server-side
      // Measurement Protocol event in /api/contact-us is a second safety net.
      router.push(THANK_YOU_PATH);
    } catch (error) {
      handleException(error as IAxiosError);
    } finally {
      setIsSubmitting(false);
      resetReCaptcha();
    }
  };

  const { handleSubmit, reset, control, setValue, watch } = useForm({
    defaultValues,
    resolver: yupResolver(contactUsSchema),
  });

  return (
    <section className="w-full bg-secondary-50 p-6 !pb-3 lg:max-w-[550px] lg:p-8 lg:!pb-5 xl:p-12 xl:!pb-9">
      <h4 className="mb-7 text-h4 font-medium text-black-900">
        How can we help you ?
      </h4>
      <p className="mb-7 text-base text-gray-500">
        Fill in this form or send us an e-mail with your inquiry.
      </p>
      <form
        onSubmit={handleSubmit(onSubmit, (errs) => {
          // form_field_error — fires once per failed submit with the
          // list of fields that errored. Tells us which inputs cause
          // friction (validation rules too strict? confusing labels?).
          const fields = Object.keys(errs);
          if (fields.length > 0) {
            trackEvent("form_field_error", {
              form_name: "contact_us",
              field_count: fields.length,
              fields: fields.join(",").slice(0, 200),
            });
          }
        })}
        onFocus={handleFormStart}
        onChange={handleFormStart}
        className="text-re flex w-full flex-col justify-center gap-4"
        id="contact-us-form"
      >
        <TInput name="firstName" control={control} placeholder="First Name *" />
        <TInput name="lastName" control={control} placeholder="Last Name *" />
        <TInput
          name="companyName"
          control={control}
          placeholder="Organization Name *"
        />
        <TInput name="workEmail" control={control} placeholder="Work Email *" />
        <TSelect
          name="position"
          control={control}
          placeholder="Position *"
          data={contactUsPositionList}
        />
        {watch("position") === "Other" && (
          <TInput
            name="specificPosition"
            control={control}
            placeholder="Specify your position *"
          />
        )}
        <TPhoneInput control={control} name="phone" />
        <TTextArea
          name="description"
          control={control}
          placeholder="Description *"
        />
        <TCheckbox
          label={<ConsentMessage />}
          checked={watch("emailConcent")}
          onChange={(e) => setValue("emailConcent", e.currentTarget.checked)}
        />

        <div>
          {/* Google reCAPTCHA component */}
          {isReCaptchaAvailable && <GoogleReCaptcha ref={reCaptchaRef} />}

          <TButton
            id="contact-us-form-submit-button"
            type="submit"
            variant="primary"
            className={clsx("h-fit", isSubmitting && "!cursor-not-allowed")}
            disabled={isSubmitting}
          >
            Submit
          </TButton>
        </div>
      </form>
    </section>
  );
};

export default ContactUsForm;
